// ============================================
// CAGED Engine - Sistema de 5 Patrones de Escala
// Basado en las cuerdas donde cae la tónica:
//   Patrón 1 (E): tónica en 6ta y 4ta cuerda
//   Patrón 2 (D): tónica en 4ta y 2da cuerda
//   Patrón 3 (C): tónica en 2da y 5ta cuerda
//   Patrón 4 (A): tónica en 5ta y 3ra cuerda
//   Patrón 5 (G): tónica en 3ra y 6ta cuerda
// ============================================

import {
  NoteName,
  NeckPosition,
  noteToSemitones,
  semitonesToNote,
  getNoteAtPosition,
  SCALE_INTERVALS,
  shouldUseFlats,
  OPEN_STRINGS,
} from "./guitarEngine";
import { type FretboardPattern, type FingeredNote } from "./fingeringEngine";

export interface CagedPattern {
  patternNumber: 1 | 2 | 3 | 4 | 5;
  shapeName: "E" | "D" | "C" | "A" | "G";
  tonicStrings: number[];
  root: NoteName;
  scaleType: string;
  positions: NeckPosition[];
  lowestFret: number;
  highestFret: number;
  span: number;
  coverage: number;
  score: number;
}

export interface PatternConnection {
  fromPattern: number;
  toPattern: number;
  fromShape: string;
  toShape: string;
  sharedString: number;
  sharedNote: NoteName;
  fromFret: number;
  toFret: number;
  description: string;
}

export interface CagedNoteMapping {
  note: NoteName;
  string: number;
  fret: number;
  midi: number;
  belongsToPatterns: number[];
  isTonicInPattern: number[];
  degree?: string;
  highlight?: string;
}

export interface CagedPath {
  patterns: CagedPattern[];
  connections: PatternConnection[];
  sequence: NeckPosition[];
}

// ─── Definiciones de los 5 patrones ───
export const CAGED_DEFINITIONS = [
  {
    patternNumber: 1 as const,
    shapeName: "E" as const,
    tonicStrings: [6, 4],
    description: "Tónica en 6ta y 4ta cuerda",
  },
  {
    patternNumber: 2 as const,
    shapeName: "D" as const,
    tonicStrings: [4, 2],
    description: "Tónica en 4ta y 2da cuerda",
  },
  {
    patternNumber: 3 as const,
    shapeName: "C" as const,
    tonicStrings: [2, 5],
    description: "Tónica en 2da y 5ta cuerda",
  },
  {
    patternNumber: 4 as const,
    shapeName: "A" as const,
    tonicStrings: [5, 3],
    description: "Tónica en 5ta y 3ra cuerda",
  },
  {
    patternNumber: 5 as const,
    shapeName: "G" as const,
    tonicStrings: [3, 6],
    description: "Tónica en 3ra y 6ta cuerda",
  },
];

// Orden cíclico en el mástil (de grave a agudo, traste bajo a alto)
export const CAGED_CYCLE: Array<1 | 2 | 3 | 4 | 5> = [1, 2, 3, 4, 5];

// Conexiones entre patrones adyacentes (cuerda compartida con tónica)
export const CAGED_ADJACENT_CONNECTIONS: {
  from: 1 | 2 | 3 | 4 | 5;
  to: 1 | 2 | 3 | 4 | 5;
  sharedString: number;
}[] = [
  { from: 1, to: 2, sharedString: 4 },
  { from: 2, to: 3, sharedString: 2 },
  { from: 3, to: 4, sharedString: 5 },
  { from: 4, to: 5, sharedString: 3 },
  { from: 5, to: 1, sharedString: 6 },
];

const DEGREE_MAP: Record<string, string[]> = {
  major: ["R", "2", "3", "4", "5", "6", "7"],
  minor: ["R", "2", "b3", "4", "5", "b6", "b7"],
  minor_harmonic: ["R", "2", "b3", "4", "5", "b6", "7"],
  minor_melodic: ["R", "2", "b3", "4", "5", "6", "7"],
  pentatonic_major: ["R", "2", "3", "5", "6"],
  pentatonic_minor: ["R", "b3", "4", "5", "b7"],
  blues: ["R", "b3", "4", "#4", "5", "b7"],
  chromatic: ["1", "b2", "2", "b3", "3", "4", "#4", "5", "b6", "6", "b7", "7"],
};

function getDegreeNames(scaleType: string): string[] {
  return DEGREE_MAP[scaleType] || DEGREE_MAP.major;
}

/**
 * Encuentra la mejor caja CAGED para un patrón específico.
 * Busca pares de tónicas en las cuerdas requeridas y construye
 * una ventana alrededor de ellas.
 */
function findBestBoxForPattern(
  root: NoteName,
  scaleType: string,
  patternDef: (typeof CAGED_DEFINITIONS)[number],
  maxFret: number
): CagedPattern | null {
  const rootSemi = noteToSemitones(root);
  const intervals = SCALE_INTERVALS[scaleType] ?? SCALE_INTERVALS.major;
  const useFlats = shouldUseFlats(root, scaleType);
  const degreeNames = getDegreeNames(scaleType);

  const tonicPositions: { string: number; fret: number; midi: number }[] = [];
  for (const s of patternDef.tonicStrings) {
    for (let f = 0; f <= maxFret; f++) {
      const pos = getNoteAtPosition(s, f);
      if (noteToSemitones(pos.note) === rootSemi) {
        tonicPositions.push({ string: s, fret: f, midi: pos.midi });
      }
    }
  }

  const candidates: {
    positions: NeckPosition[];
    tonicFrets: number[];
    score: number;
  }[] = [];

  for (let i = 0; i < tonicPositions.length; i++) {
    for (let j = i + 1; j < tonicPositions.length; j++) {
      const t1 = tonicPositions[i];
      const t2 = tonicPositions[j];

      if (t1.string === t2.string) continue;

      const hasRequired = patternDef.tonicStrings.every(
        (rs) => rs === t1.string || rs === t2.string
      );
      if (!hasRequired) continue;

      const fretSpan = Math.abs(t1.fret - t2.fret);
      if (fretSpan > 6) continue;

      const centerFret = Math.round((t1.fret + t2.fret) / 2);
      const windowStart = Math.max(0, centerFret - 2);
      const windowEnd = Math.min(maxFret, centerFret + 2);

      const boxPositions: NeckPosition[] = [];
      for (let s = 6; s >= 1; s--) {
        for (let f = windowStart; f <= windowEnd; f++) {
          const pos = getNoteAtPosition(s, f);
          const posSemi = noteToSemitones(pos.note);
          const matched = intervals.find(
            (int) => ((rootSemi + int) % 12) === posSemi
          );
          if (matched !== undefined) {
            const degreeIdx = intervals.indexOf(matched);
            const degree = degreeNames[degreeIdx];
            boxPositions.push({
              string: s,
              fret: f,
              note: semitonesToNote(posSemi, useFlats),
              degree: degree as any,
              highlight:
                degree === "R"
                  ? "root"
                  : degree === "3" || degree === "b3"
                  ? "third"
                  : degree === "5"
                  ? "fifth"
                  : "scale",
            });
          }
        }
      }

      if (boxPositions.length >= 6) {
        const frets = boxPositions.map((p) => p.fret);
        const span = Math.max(...frets) - Math.min(...frets);
        const uniqueDegrees = new Set(boxPositions.map((p) => p.degree)).size;
        const score =
          uniqueDegrees * 15 +
          boxPositions.length * 2 -
          span * 4 -
          Math.abs(span - 3) * 2;
        candidates.push({
          positions: boxPositions,
          tonicFrets: [t1.fret, t2.fret],
          score,
        });
      }
    }
  }

  if (candidates.length === 0) return null;

  candidates.sort((a, b) => b.score - a.score);
  const best = candidates[0];

  const frets = best.positions.map((p) => p.fret);
  const lowest = Math.min(...frets);
  const highest = Math.max(...frets);

  return {
    patternNumber: patternDef.patternNumber,
    shapeName: patternDef.shapeName,
    tonicStrings: patternDef.tonicStrings,
    root,
    scaleType,
    positions: best.positions,
    lowestFret: lowest,
    highestFret: highest,
    span: highest - lowest,
    coverage: new Set(best.positions.map((p) => p.degree)).size,
    score: best.score,
  };
}

// ─── API Pública ───

export function generateCagedPatterns(
  root: NoteName,
  scaleType: string,
  maxFret: number = 15
): CagedPattern[] {
  const results: CagedPattern[] = [];

  for (const def of CAGED_DEFINITIONS) {
    const pattern = findBestBoxForPattern(root, scaleType, def, maxFret);
    if (pattern) {
      results.push(pattern);
    }
  }

  results.sort((a, b) => a.lowestFret - b.lowestFret);
  return results;
}

export function generateExtendedCagedPatterns(
  root: NoteName,
  scaleType: string,
  octaves: number = 2,
  maxFret: number = 15
): CagedPattern[] {
  const basePatterns = generateCagedPatterns(root, scaleType, maxFret);
  if (basePatterns.length === 0) return [];

  const results: CagedPattern[] = [];

  for (let oct = 0; oct < octaves; oct++) {
    for (const base of basePatterns) {
      const shiftedPositions: NeckPosition[] = base.positions
        .map((p) => {
          const newFret = p.fret + oct * 12;
          if (newFret > maxFret) return null;
          const pos = getNoteAtPosition(p.string, newFret);
          return {
            ...p,
            fret: newFret,
            note: pos.note,
          };
        })
        .filter(Boolean) as NeckPosition[];

      if (shiftedPositions.length === 0) continue;

      const frets = shiftedPositions.map((p) => p.fret);
      results.push({
        ...base,
        positions: shiftedPositions,
        lowestFret: Math.min(...frets),
        highestFret: Math.max(...frets),
      });
    }
  }

  return results.sort((a, b) => a.lowestFret - b.lowestFret);
}

export function findPatternConnections(
  patterns: CagedPattern[]
): PatternConnection[] {
  const connections: PatternConnection[] = [];

  for (const adj of CAGED_ADJACENT_CONNECTIONS) {
    const fromPattern = patterns.find((p) => p.patternNumber === adj.from);
    const toPattern = patterns.find((p) => p.patternNumber === adj.to);
    if (!fromPattern || !toPattern) continue;

    const fromTonicNotes = fromPattern.positions.filter(
      (p) => p.degree === "R" && p.string === adj.sharedString
    );
    const toTonicNotes = toPattern.positions.filter(
      (p) => p.degree === "R" && p.string === adj.sharedString
    );

    for (const fn of fromTonicNotes) {
      for (const tn of toTonicNotes) {
        if (noteToSemitones(fn.note) === noteToSemitones(tn.note)) {
          connections.push({
            fromPattern: adj.from,
            toPattern: adj.to,
            fromShape: fromPattern.shapeName,
            toShape: toPattern.shapeName,
            sharedString: adj.sharedString,
            sharedNote: fn.note,
            fromFret: fn.fret,
            toFret: tn.fret,
            description: `${fromPattern.shapeName} → ${toPattern.shapeName} por cuerda ${adj.sharedString} (${fn.note})`,
          });
          break;
        }
      }
    }
  }

  return connections;
}

export function mapNoteToCaged(
  stringNum: number,
  fret: number,
  patterns: CagedPattern[]
): CagedNoteMapping {
  const pos = getNoteAtPosition(stringNum, fret);

  const belongsToPatterns: number[] = [];
  const isTonicInPattern: number[] = [];
  let degree: string | undefined;
  let highlight: string | undefined;

  for (const pattern of patterns) {
    const match = pattern.positions.find(
      (p) => p.string === stringNum && p.fret === fret
    );
    if (match) {
      belongsToPatterns.push(pattern.patternNumber);
      degree = match.degree;
      highlight = match.highlight;
      if (match.degree === "R" || match.highlight === "root") {
        isTonicInPattern.push(pattern.patternNumber);
      }
    }
  }

  return {
    note: pos.note,
    string: stringNum,
    fret,
    midi: pos.midi,
    belongsToPatterns,
    isTonicInPattern,
    degree,
    highlight,
  };
}

export function getAllNotePositionsWithCaged(
  note: NoteName,
  patterns: CagedPattern[],
  maxFret: number = 15
): CagedNoteMapping[] {
  const positions: CagedNoteMapping[] = [];

  for (let s = 1; s <= 6; s++) {
    for (let f = 0; f <= maxFret; f++) {
      const pos = getNoteAtPosition(s, f);
      if (noteToSemitones(pos.note) === noteToSemitones(note)) {
        positions.push(mapNoteToCaged(s, f, patterns));
      }
    }
  }

  return positions;
}

export function buildCagedMixedSequence(
  patterns: CagedPattern[],
  options: {
    direction?: "asc" | "desc" | "both";
    octaves?: number;
    connectionMode?: "tonic" | "nearest" | "full" | "overlap";
  } = {}
): NeckPosition[] {
  const {
    direction = "asc",
    octaves = 1,
    connectionMode = "overlap",
  } = options;

  if (patterns.length === 0) return [];

  const sorted = [...patterns].sort((a, b) => a.lowestFret - b.lowestFret);
  const result: NeckPosition[] = [];

  for (let oct = 0; oct < octaves; oct++) {
    for (let i = 0; i < sorted.length; i++) {
      const pattern = sorted[i];

      const notes = [...pattern.positions].sort((a, b) => {
        const midiA = OPEN_STRINGS[6 - a.string].midi + a.fret;
        const midiB = OPEN_STRINGS[6 - b.string].midi + b.fret;
        return midiA - midiB;
      });

      let ordered: NeckPosition[] = [];
      if (direction === "asc") {
        ordered = notes;
      } else if (direction === "desc") {
        ordered = [...notes].reverse();
      } else {
        ordered = [...notes, ...[...notes].reverse().slice(1, -1)];
      }

      for (const n of ordered) {
        const last = result[result.length - 1];
        if (last && last.string === n.string && last.fret === n.fret) {
          continue;
        }
        result.push(n);
      }

      if (i < sorted.length - 1 && connectionMode !== "full") {
        const nextPattern = sorted[i + 1];

        if (connectionMode === "tonic") {
          const tonicNotes = nextPattern.positions.filter((p) => p.degree === "R");
          if (tonicNotes.length > 0) {
            const lastNote = result[result.length - 1];
            let bestTonic = tonicNotes[0];
            let bestDist = Infinity;
            for (const tn of tonicNotes) {
              const dist =
                Math.abs(tn.string - lastNote.string) +
                Math.abs(tn.fret - lastNote.fret);
              if (dist < bestDist) {
                bestDist = dist;
                bestTonic = tn;
              }
            }
            if (!result.some((r) => r.string === bestTonic.string && r.fret === bestTonic.fret)) {
              result.push({ ...bestTonic, highlight: "root" });
            }
          }
        } else if (connectionMode === "overlap") {
          const shared: NeckPosition[] = [];
          for (const np of nextPattern.positions) {
            const alreadyPlayed = pattern.positions.some(
              (cp) => noteToSemitones(cp.note) === noteToSemitones(np.note)
            );
            if (alreadyPlayed) {
              shared.push(np);
            }
          }
          if (shared.length > 0) {
            const lastNote = result[result.length - 1];
            let best = shared[0];
            let bestDist = Infinity;
            for (const s of shared) {
              const dist =
                Math.abs(s.string - lastNote.string) +
                Math.abs(s.fret - lastNote.fret);
              if (dist < bestDist) {
                bestDist = dist;
                best = s;
              }
            }
            if (!result.some((r) => r.string === best.string && r.fret === best.fret)) {
              result.push(best);
            }
          }
        }
      }
    }
  }

  return result;
}

export function findCagedPathBetweenFrets(
  patterns: CagedPattern[],
  startFret: number,
  endFret: number
): CagedPattern[] {
  const sorted = [...patterns].sort((a, b) => a.lowestFret - b.lowestFret);
  const included: CagedPattern[] = [];

  for (const p of sorted) {
    if (
      (p.lowestFret >= startFret && p.lowestFret <= endFret) ||
      (p.highestFret >= startFret && p.highestFret <= endFret) ||
      (p.lowestFret <= startFret && p.highestFret >= endFret)
    ) {
      included.push(p);
    }
  }

  return included;
}

export function suggestNextPattern(
  currentPatternNumber: 1 | 2 | 3 | 4 | 5,
  direction: "up" | "down" | "cycle" = "cycle"
): number {
  const idx = CAGED_CYCLE.indexOf(currentPatternNumber);
  if (idx === -1) return 1;

  if (direction === "up") {
    return CAGED_CYCLE[(idx + 1) % CAGED_CYCLE.length];
  }
  if (direction === "down") {
    return CAGED_CYCLE[(idx - 1 + CAGED_CYCLE.length) % CAGED_CYCLE.length];
  }
  return CAGED_CYCLE[(idx + 1) % CAGED_CYCLE.length];
}

// ─── Normalización a FretboardPattern ───

export function toFretboardPattern(pattern: CagedPattern): FretboardPattern {
  const lowest = pattern.lowestFret;
  const notes: FingeredNote[] = pattern.positions.map((p) => ({
    string: p.string,
    fret: p.fret,
    note: p.note,
    finger: p.fret === 0 ? 0 : Math.min(Math.max(p.fret - lowest + 1, 1), 4),
    degree: (p.degree ?? "R") as string,
  }));

  return {
    type: 'caged',
    id: `caged-${pattern.patternNumber}`,
    label: pattern.shapeName,
    position: lowest,
    notes,
    span: pattern.span,
    score: pattern.score,
    stringRange: [
      Math.max(...notes.map((n) => n.string)),
      Math.min(...notes.map((n) => n.string)),
    ] as [number, number],
    coverage: pattern.coverage,
    lowestFret: pattern.lowestFret,
    highestFret: pattern.highestFret,
  };
}

// ─── Guía de transición entre cajas ───

export interface TransitionGuide {
  pivotFinger?: number;
  handMovement: 'slide' | 'shift' | 'jump';
  message: string;
}

export function getTransitionGuide(a: FretboardPattern, b: FretboardPattern): TransitionGuide {
  // 1. Nota compartida exacta → dedo pivote
  for (const na of a.notes) {
    for (const nb of b.notes) {
      if (na.string === nb.string && na.fret === nb.fret && na.finger != null && na.finger > 0) {
        return {
          pivotFinger: na.finger,
          handMovement: 'slide',
          message: `Mantén dedo ${na.finger} en cuerda ${na.string}`,
        };
      }
    }
  }

  // 2. Nota cercana (±1 traste) en misma cuerda → deslizar
  for (const na of a.notes) {
    for (const nb of b.notes) {
      if (
        na.string === nb.string &&
        Math.abs(na.fret - nb.fret) <= 1 &&
        na.finger != null &&
        na.finger > 0
      ) {
        return {
          pivotFinger: na.finger,
          handMovement: 'shift',
          message: `Desliza dedo ${na.finger} del traste ${na.fret} al ${nb.fret}`,
        };
      }
    }
  }

  // 3. Salto
  const fretDistance = Math.abs(b.lowestFret - a.highestFret);
  if (fretDistance > 3) {
    return {
      handMovement: 'jump',
      message: `Salto de mano del traste ${a.highestFret} al ${b.lowestFret}`,
    };
  }

  // Fallback: cambio de posición genérico
  return {
    handMovement: 'shift',
    message: `Cambia de posición ${a.label} a ${b.label}`,
  };
}

export function getCagedNoteContext(
  stringNum: number,
  fret: number,
  root: NoteName,
  patterns: CagedPattern[]
): {
  note: NoteName;
  isTonic: boolean;
  isThird: boolean;
  isFifth: boolean;
  patterns: { number: number; shape: string; degree: string }[];
  connections: PatternConnection[];
} {
  const mapping = mapNoteToCaged(stringNum, fret, patterns);
  const pos = getNoteAtPosition(stringNum, fret);
  const rootSemi = noteToSemitones(root);
  const noteSemi = noteToSemitones(pos.note);
  const interval = ((noteSemi - rootSemi) + 12) % 12;

  const patternDetails = mapping.belongsToPatterns.map((pn) => {
    const pat = patterns.find((p) => p.patternNumber === pn);
    const match = pat?.positions.find(
      (p) => p.string === stringNum && p.fret === fret
    );
    return {
      number: pn,
      shape: pat?.shapeName || "?",
      degree: match?.degree || "?",
    };
  });

  const allConnections = findPatternConnections(patterns);
  const relevantConnections = allConnections.filter(
    (c) =>
      mapping.belongsToPatterns.includes(c.fromPattern) ||
      mapping.belongsToPatterns.includes(c.toPattern)
  );

  return {
    note: pos.note,
    isTonic: interval === 0,
    isThird: interval === 3 || interval === 4,
    isFifth: interval === 7,
    patterns: patternDetails,
    connections: relevantConnections,
  };
}
